import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { Papa, ParseResult } from 'ngx-papaparse';
import { RRule } from 'rrule';
import PhoneNumber from 'awesome-phonenumber';
import { Strings } from 'src/app/text/strings';
import log from 'loglevel';

export interface Validator<T> {
  isValid(value: T): boolean;
}

export interface Formatter<T> {
  format(value: T): string;
}

class ValidationEntry {
  constructor(public valid: boolean = false, public message?: string | undefined) {}
}

class MatchResult {
  percentMatch: number = 0;
  percentMatchWithContent: number = 0;
  percentNonMatch: number = 0;
  rowsMatch: number = 0;
  rowsNonMatch: number = 0;
  score: number = 0;
  rows: ValidationEntry[] = [];
  valid = false;
}

export class RRuleValidator implements Validator<string> {
  isValid(value: string): boolean {
    let valid = false;
    try {
      RRule.parseString(value);
      valid = true;
    } catch (error) { }
    return valid;
  }
}

export class PhoneNumberValidator implements Validator<string>, Formatter<string> {

  constructor(private defaultCountryCode: string) { }

  isValid(value: string): boolean {
    if(Strings.isBlank(value)) {
      return true;
    }

    try {
      let pn = new PhoneNumber(value, this.defaultCountryCode);
      let result = pn.isValid();
      log.debug(`Validating phonenumber ${value}: ${result}`)
      return result;
    } catch (error) {
      log.debug(`Cannot parse phonenumber: ${value}`);
      return false;
    }
  }

  format(value: string) {
    if(Strings.isBlank(value)) {
      return (null as any) as string;
    }

    let pn = new PhoneNumber(value, this.defaultCountryCode);
    let result = pn.getNumber();
    log.debug(`Formatting phonenumber ${value}: ${result}`)
    return result;
  }
}

export class StringMatchValidator implements Validator<string> {

  constructor(private matchPattern: RegExp) { }

  isValid(value: string): boolean {
    return this.matchPattern.test(value);
  }
}

export class ImportField<T> {
  valid: boolean | null = null;
  matchField?: string;
  matchPercentage: number = 0;
  nonMatchPercentage: number = 0;
  matchRows: number = 0;
  nonMatchRows: number = 0;
  matchScore: number = 0;
  approved = false;
  ignored = false;
  rows: ValidationEntry[] | undefined;

  constructor(public name: string, public aliases: string[], public displayName: string, public description: string,
    public required = false, public validator?: Validator<T>, public formatter?: Formatter<T>) { }
}

export interface DataBuilder<T> {
  buildData(fields: ImportField<any>[], data: ParseResult): T;
}

export interface DataImporter<T> {
  importData(data: T, progressIndicator: IProgressIndicator): void;
}

export interface IProgressIndicator {
  progress(percentage: number, message: string): void;
  complete(message: string): void;
  error(message: string): void;
}

@Component({
  selector: 'app-csv-importer',
  templateUrl: './csv-importer.component.html',
  styleUrls: ['./csv-importer.component.scss']
})
export class CsvImporterComponent {

  phoneAdaptor = new PhoneNumberValidator('DK');

  @Input() fields?: ImportField<any>[];
  @Input() dataBuilder?: DataBuilder<any>;
  @Input() dataImporter?: DataImporter<any>;
  @Output() step = new EventEmitter<string>();

  steps = ['upload', 'parsing', 'match', 'import', 'done'];
  current = 'upload';
  currentImport?: ParseResult;
  progressPercentage = 0.0;
  progressText = '';
  importError?: string;

  constructor(private papa: Papa) { }
 
  get currentStep() {
    return this.steps.indexOf(this.current) + 1;
  }

  pre(): void {
    let index = this.steps.indexOf(this.current);
    index = Math.max(0, index - 1);
    this.current = this.steps[index];
    this.step.emit(this.current);
  }

  next(): void {
    if (this.current === 'match') {
      const data = this.dataBuilder?.buildData(this.fields!, this.currentImport!);
      this.dataImporter?.importData(data, {
        progress: (percentage, message) => {
          this.progressPercentage = this.progressPercentage;
          this.progressText = message;
        },
        complete: (message) => {
          this.next();
        },
        error: (message) => {
          this.importError = message!;
          this.next();
        }
      });
    }

    let index = this.steps.indexOf(this.current);
    index = Math.min(this.steps.length - 1, index + 1);
    this.current = this.steps[index];
    this.step.emit(this.current);
  }

  isNextShown() {
    return this.current === 'match' || this.current === 'import' || this.current === 'verify';
  }

  isNextDisabled() {
    return false;/*
    if(this.current === 'match') {
      let hasNonHandledField = false;
      this.fields!.forEach(f => {
        if(!f.approved && !f.ignored) {
          hasNonHandledField = true;
        }
      })
      return hasNonHandledField;
    } else {  
      return false;
    }*/
  }

  isDoneShown() {
    return this.current === 'done';
  }

  done(): void {
    log.debug('done');
  }

  onFileParse(result: ParseResult) {
    this.current = 'parsing';
    setTimeout(() => this.doFileParse(result), 50);
  }

  doFileParse(result: ParseResult) {
    this.currentImport = result;
    this.fields?.forEach(expectedField => {
      let bestGuess: string;
      let bestScore = -1000.0;
      let bestGuessValid = false;
      let bestGuessMatchPercentage = 0;
      let bestGuessNonMatchPercentage = 0;
      let bestGuessMatchRows = 0;
      let bestGuessNonMatchRows = 0;
      let bestGuessRows: ValidationEntry[] | undefined;
      let exactNameMatchFound = false;
      expectedField.valid = !expectedField.required;

      result.meta.fields.forEach(receivedField => {
        if (exactNameMatchFound) return;
        let score = 0.0;
        let exactNameMatch = false;
        score = this.calculateNameScore(score, expectedField, receivedField);

        if (score == 0.0) {
          exactNameMatch = true;
        }

        let matchResult = this.calculateMatchResult(result, receivedField, expectedField);
        score -= matchResult.score;

        if ((matchResult.percentMatchWithContent > 0 && score > bestScore) || exactNameMatch) {
          bestGuess = receivedField;
          bestScore = score;
          bestGuessMatchPercentage = matchResult.percentMatchWithContent;
          bestGuessNonMatchPercentage = matchResult.percentNonMatch;
          bestGuessMatchRows = matchResult.rowsMatch;
          bestGuessNonMatchRows = matchResult.rowsNonMatch;
          bestGuessRows = matchResult.rows;
          bestGuessValid = matchResult.valid;

          if (exactNameMatch) {
            exactNameMatchFound = true;
          }
        }
      });

      let bestMatch = true;
      this.fields?.forEach(reflectionField => {
        if (reflectionField.matchField === bestGuess && reflectionField.matchScore > bestScore) {
          bestMatch = false;
        } else if (reflectionField.matchField === bestGuess) {
          reflectionField.matchField = undefined;
          reflectionField.matchPercentage = 0;
          reflectionField.matchScore = 0;
        }
      });

      if (bestMatch) {
        expectedField.matchField = bestGuess!;
        expectedField.matchPercentage = bestGuessMatchPercentage;
        expectedField.matchScore = bestScore;
        expectedField.nonMatchPercentage = bestGuessNonMatchPercentage;
        expectedField.matchRows = bestGuessMatchRows;
        expectedField.nonMatchRows = bestGuessNonMatchRows;
        expectedField.rows = bestGuessRows;
        expectedField.valid = bestGuessValid;
      }
    });

    this.fields?.forEach(field => {
      log.debug(`BEST GUESS: ${field.name}->${field.matchField}: ${field.matchPercentage}% / ${field.matchScore}  / ${field.nonMatchPercentage}`);
    })

    this.current = 'match';
  }

  private updateCalculatedMatch(field: ImportField<any>) {
    field.rows = undefined;
    if(field.matchField == null) {
      field.matchPercentage = 0;
      field.nonMatchPercentage = 0;
      field.matchScore = 0;
      field.valid = !field.required;
    } else {
      this.currentImport?.meta.fields.forEach(receivedField => {
        if(field.matchField === receivedField) {
          let matchResult = this.calculateMatchResult(this.currentImport!, receivedField, field);
          field.matchScore = matchResult.score;
          field.matchPercentage = matchResult.percentMatch;
          field.nonMatchPercentage = matchResult.percentNonMatch;
          field.matchRows = matchResult.rowsMatch;
          field.nonMatchRows = matchResult.rowsNonMatch;
          field.rows = matchResult.rows;
          field.valid = matchResult.valid;
        }
      });
    }
  }

  private calculateMatchResult(result: ParseResult, receivedField: string, expectedField: ImportField<any>): MatchResult {
    let dataRows = 0;
    let matchingRows = 0;
    let matchingRowsWithContent = 0;
    let validField = true;
    let nonMatchingRows = 0;
    let matchResult = new MatchResult();
    let validationArray: ValidationEntry[]= [];

    (result.data).forEach((element: any) => {
      const validation = new ValidationEntry(true);
      validationArray.push(validation);

      if(Object.keys(element).length === 1 && (element[Object.keys(element)[0]] as string).trim() === '') {
        return;
      }

      dataRows++;
      const value = element[receivedField] as string;
      const hasValue = value != null && value.trim() !== '';
      const hasValidator = expectedField.validator != null;
      const required = expectedField.required;
      const valid = hasValidator ? expectedField?.validator?.isValid(value) : true;
      let passesValidation = false;
      
      if(!hasValue && !required) {
        passesValidation = true;
      }

      if(hasValue && valid) {
        passesValidation = true;
      }
      
      if(!passesValidation) {
        nonMatchingRows++;
        validField = false;
        validation.valid = false;
      }

      if(passesValidation) {
        matchingRows++;
      }

      if(hasValue && passesValidation) {
        matchingRowsWithContent++;
      }

    });
    matchResult.rowsMatch = matchingRows;
    matchResult.rowsNonMatch = nonMatchingRows;
    matchResult.percentMatch = matchingRows === 0 ? 0 : matchingRows / dataRows * 100;
    matchResult.percentMatchWithContent = matchingRowsWithContent === 0 ? 0 : matchingRowsWithContent / dataRows * 100;
    matchResult.percentNonMatch = nonMatchingRows === 0 ? 0 : nonMatchingRows / dataRows * 100;
    matchResult.score = (100 - matchResult.percentMatch);
    matchResult.score = (100 - matchResult.percentMatchWithContent) / 100;
    matchResult.score = (100 - matchResult.percentNonMatch) / 100;
    matchResult.rows = validationArray;
    matchResult.valid = validField;
    return matchResult;
  }

  private calculateNameScore(score: number, expectedField: ImportField<any>, receivedField: string) {
    score -= Strings.distance(expectedField.name.toLocaleLowerCase(), receivedField.toLocaleLowerCase());
    if (expectedField.aliases && expectedField.aliases.length > 0) {
      expectedField.aliases.forEach(a => {
        let aliasScore = 0.0;
        aliasScore -= Strings.distance(a.toLocaleLowerCase(), receivedField.toLocaleLowerCase());
        if (aliasScore > score) {
          score = aliasScore;
        }
      });
    }
    return score;
  }

  onMatchComplete() {
    Stallion.confirm($localize `Are you sure you want to import these data?`, 'warning', $localize `Cancel`, $localize `Yes, import data`).then(result => {
      if(result === true) {
        this.next();
      }
    });
    
  }

  onMatchChange(field: ImportField<any>) {
    log.debug('Field changed:' + field);
    this.updateCalculatedMatch(field);
  }

}
